// frontend/src/components/Portfolio/PortfolioHealthScore.tsx
// Comprehensive Portfolio Health Score combining:
// - Overall risk score (weighted by asset value)
// - Geographic diversification score
// - Community resilience score
// - Insurance coverage adequacy

import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Tooltip,
  IconButton,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Alert,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  TrendingUp as RiskIcon,
  Public as GeoIcon,
  Groups as CommunityIcon,
  Security as InsuranceIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

interface PortfolioItem {
  postal_code?: string;
  city?: string;
  state?: string;
  asset_value?: number | string;
  risk_score?: number;
  resilience?: number;
  com_risk?: number;
  latitude?: number;
  longitude?: number;
  flood?: number;
  wildfire?: number;
  hurricane?: number;
  earthquake?: number;
  tornado?: number;
  [key: string]: any;
}

interface PortfolioHealthScoreProps {
  portfolio: {
    items: PortfolioItem[];
    name?: string;
    total_value?: number;
    [key: string]: any;
  };
}

// Normalize asset value helper
const normalizeAssetValue = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const cleaned = String(value).replace(/[$,]/g, '');
  return parseFloat(cleaned) || 0;
};

// Format currency
const formatCurrency = (value: number): string => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

// Get score color
const getScoreColor = (score: number, inverse: boolean = false): string => {
  const effectiveScore = inverse ? 100 - score : score;
  if (effectiveScore >= 75) return '#10B981'; // Green
  if (effectiveScore >= 50) return '#F59E0B'; // Amber
  if (effectiveScore >= 25) return '#F97316'; // Orange
  return '#EF4444'; // Red
};

// Get grade from score
const getGrade = (score: number): string => {
  if (score >= 90) return 'A+';
  if (score >= 80) return 'A';
  if (score >= 70) return 'B';
  if (score >= 60) return 'C';
  if (score >= 50) return 'D';
  return 'F';
};

// Score Card Component
const ScoreCard = ({ 
  title, 
  score, 
  icon, 
  color, 
  description, 
  details,
  warnings,
  onInfoClick 
}: {
  title: string;
  score: number;
  icon: React.ReactNode;
  color: string;
  description: string;
  details?: string[];
  warnings?: string[];
  onInfoClick?: () => void;
}) => (
  <Paper 
    sx={{ 
      p: 2.5, 
      height: '100%',
      border: '1px solid #E5E7EB',
      transition: 'all 0.2s',
      '&:hover': { boxShadow: 3 }
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ 
          p: 1, 
          borderRadius: 2, 
          bgcolor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
          {title}
        </Typography>
      </Box>
      {onInfoClick && (
        <Tooltip title="View details">
          <IconButton size="small" onClick={onInfoClick}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
      <Typography variant="h3" fontWeight={700} sx={{ color }}>
        {score.toFixed(0)}
      </Typography>
      <Typography variant="body2" color="text.secondary">/100</Typography>
      <Chip 
        label={getGrade(score)} 
        size="small" 
        sx={{ 
          bgcolor: `${color}20`, 
          color, 
          fontWeight: 700,
          ml: 'auto'
        }} 
      />
    </Box>
    
    <LinearProgress 
      variant="determinate" 
      value={score} 
      sx={{ 
        height: 6, 
        borderRadius: 3, 
        mb: 1.5,
        bgcolor: '#E5E7EB',
        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 }
      }} 
    />
    
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      {description}
    </Typography>
    
    {warnings && warnings.length > 0 && (
      <Box sx={{ mt: 1 }}>
        {warnings.slice(0, 2).map((warning, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
            <WarningIcon sx={{ fontSize: 14, color: '#F59E0B', mt: 0.3 }} />
            <Typography variant="caption" color="text.secondary">
              {warning}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Paper>
);

export default function PortfolioHealthScore({ portfolio }: PortfolioHealthScoreProps) {
  const [detailDialog, setDetailDialog] = useState<string | null>(null);
  
  const items = portfolio?.items || [];
  
  // ============================================================================
  // 1. OVERALL RISK SCORE (Weighted by Asset Value)
  // ============================================================================
  const riskMetrics = useMemo(() => {
    if (items.length === 0) return { 
      score: 0, 
      weightedRisk: 0, 
      highRiskValue: 0,
      highRiskPct: 0,
      highRiskCount: 0,
      totalValue: 0,
      warnings: [] as string[], 
      topRiskLocations: [] as { risk: number; value: number; location: string }[]
    };
    
    let totalWeightedRisk = 0;
    let totalValue = 0;
    let highRiskValue = 0;
    let highRiskCount = 0;
    
    const riskByValue: { risk: number; value: number; location: string }[] = [];
    
    items.forEach(item => {
      const value = normalizeAssetValue(item.asset_value);
      const risk = parseFloat(String(item.risk_score || 0));
      
      if (value > 0) {
        totalWeightedRisk += risk * value;
        totalValue += value;
        riskByValue.push({ 
          risk, 
          value, 
          location: `${item.city || 'Unknown'}, ${item.state || ''}` 
        });
        
        if (risk > 6) {
          highRiskValue += value;
          highRiskCount++;
        }
      }
    });
    
    const weightedAvgRisk = totalValue > 0 ? totalWeightedRisk / totalValue : 0;
    // Convert risk (0-10) to score (0-100) where lower risk = higher score
    const score = Math.max(0, Math.min(100, (10 - weightedAvgRisk) * 10));
    
    const warnings: string[] = [];
    const highRiskPct = totalValue > 0 ? (highRiskValue / totalValue) * 100 : 0;
    
    if (highRiskPct > 50) {
      warnings.push(`${highRiskPct.toFixed(0)}% of value in high-risk locations`);
    }
    if (highRiskCount > items.length * 0.3) {
      warnings.push(`${highRiskCount} of ${items.length} locations are high-risk`);
    }
    
    // Top 5 highest risk by value
    const topRiskLocations = riskByValue
      .sort((a, b) => (b.risk * b.value) - (a.risk * a.value))
      .slice(0, 5);
    
    return { 
      score, 
      weightedRisk: weightedAvgRisk,
      highRiskValue,
      highRiskPct,
      highRiskCount,
      totalValue,
      warnings,
      topRiskLocations
    };
  }, [items]);

  // ============================================================================
  // 2. GEOGRAPHIC DIVERSIFICATION SCORE
  // ============================================================================
  const geoMetrics = useMemo(() => {
    if (items.length === 0) return { 
      score: 0, 
      hhi: 0,
      stateCount: 0,
      regionCount: 0,
      stateBreakdown: [] as { state: string; value: number; pct: number }[],
      regionBreakdown: [] as { region: string; value: number; pct: number }[],
      warnings: [] as string[], 
      totalValue: 0
    };
    
    const stateValues: Record<string, number> = {};
    const regionValues: Record<string, number> = {};
    let totalValue = 0;
    
    // Define US regions
    const stateToRegion: Record<string, string> = {
      // Northeast
      CT: 'Northeast', DE: 'Northeast', MA: 'Northeast', MD: 'Northeast', ME: 'Northeast',
      NH: 'Northeast', NJ: 'Northeast', NY: 'Northeast', PA: 'Northeast', RI: 'Northeast', VT: 'Northeast',
      // Southeast
      AL: 'Southeast', AR: 'Southeast', FL: 'Southeast', GA: 'Southeast', KY: 'Southeast',
      LA: 'Southeast', MS: 'Southeast', NC: 'Southeast', SC: 'Southeast', TN: 'Southeast',
      VA: 'Southeast', WV: 'Southeast', DC: 'Southeast',
      // Midwest
      IA: 'Midwest', IL: 'Midwest', IN: 'Midwest', KS: 'Midwest', MI: 'Midwest',
      MN: 'Midwest', MO: 'Midwest', ND: 'Midwest', NE: 'Midwest', OH: 'Midwest',
      SD: 'Midwest', WI: 'Midwest',
      // Southwest
      AZ: 'Southwest', NM: 'Southwest', OK: 'Southwest', TX: 'Southwest',
      // West
      AK: 'West', CA: 'West', CO: 'West', HI: 'West', ID: 'West', MT: 'West',
      NV: 'West', OR: 'West', UT: 'West', WA: 'West', WY: 'West',
    };
    
    items.forEach(item => {
      const value = normalizeAssetValue(item.asset_value);
      const state = item.state || 'Unknown';
      const region = stateToRegion[state] || 'Other';
      
      stateValues[state] = (stateValues[state] || 0) + value;
      regionValues[region] = (regionValues[region] || 0) + value;
      totalValue += value;
    });
    
    // Calculate concentration metrics
    const stateConcentrations = Object.entries(stateValues)
      .map(([state, value]) => ({ state, value, pct: totalValue > 0 ? (value / totalValue) * 100 : 0 }))
      .sort((a, b) => b.value - a.value);
    
    const regionConcentrations = Object.entries(regionValues)
      .map(([region, value]) => ({ region, value, pct: totalValue > 0 ? (value / totalValue) * 100 : 0 }))
      .sort((a, b) => b.value - a.value);
    
    // Herfindahl-Hirschman Index for states (lower = more diversified)
    const hhi = stateConcentrations.reduce((sum, s) => sum + Math.pow(s.pct, 2), 0);
    
    // Convert HHI to score (HHI ranges from ~0 to 10000)
    // HHI < 1500 = highly diversified, HHI > 2500 = concentrated
    const maxHHI = 10000; // All in one state
    const score = Math.max(0, Math.min(100, (1 - hhi / maxHHI) * 100));
    
    const warnings: string[] = [];
    const topState = stateConcentrations[0];
    const topRegion = regionConcentrations[0];
    
    if (topState && topState.pct > 40) {
      warnings.push(`${topState.pct.toFixed(0)}% concentrated in ${topState.state}`);
    }
    if (topRegion && topRegion.pct > 60) {
      warnings.push(`${topRegion.pct.toFixed(0)}% in ${topRegion.region} region`);
    }
    if (Object.keys(stateValues).length < 3) {
      warnings.push(`Only ${Object.keys(stateValues).length} state(s) represented`);
    }
    
    return {
      score,
      hhi,
      stateCount: Object.keys(stateValues).length,
      regionCount: Object.keys(regionValues).length,
      stateBreakdown: stateConcentrations.slice(0, 10),
      regionBreakdown: regionConcentrations,
      warnings,
      totalValue
    };
  }, [items]);

  // ============================================================================
  // 3. COMMUNITY RESILIENCE SCORE
  // ============================================================================
  const resilienceMetrics = useMemo(() => {
    if (items.length === 0) return { 
      score: 0, 
      avgResilience: 5,
      avgVulnerability: 5,
      lowResilienceCount: 0,
      lowResilienceValue: 0,
      lowResPct: 0,
      warnings: [] as string[], 
      lowestResilience: [] as { location: string; resilience: number; vulnerability: number; value: number }[],
      totalValue: 0
    };
    
    let totalWeightedResilience = 0;
    let totalWeightedVulnerability = 0;
    let totalValue = 0;
    let lowResilienceCount = 0;
    let lowResilienceValue = 0;
    
    const locationScores: { location: string; resilience: number; vulnerability: number; value: number }[] = [];
    
    items.forEach(item => {
      const value = normalizeAssetValue(item.asset_value);
      const resilience = parseFloat(String(item.resilience || 5)); // Default to 5 if not available
      const vulnerability = parseFloat(String(item.com_risk || 5));
      
      if (value > 0) {
        totalWeightedResilience += resilience * value;
        totalWeightedVulnerability += vulnerability * value;
        totalValue += value;
        
        locationScores.push({
          location: `${item.city || 'Unknown'}, ${item.state || ''}`,
          resilience,
          vulnerability,
          value
        });
        
        if (resilience < 4) {
          lowResilienceCount++;
          lowResilienceValue += value;
        }
      }
    });
    
    const avgResilience = totalValue > 0 ? totalWeightedResilience / totalValue : 5;
    const avgVulnerability = totalValue > 0 ? totalWeightedVulnerability / totalValue : 5;
    
    // Score: Higher resilience and lower vulnerability = higher score
    // Resilience (0-10) where 10 is best, Vulnerability (0-10) where 0 is best
    const score = Math.max(0, Math.min(100, ((avgResilience * 10) + ((10 - avgVulnerability) * 10)) / 2));
    
    const warnings: string[] = [];
    const lowResPct = totalValue > 0 ? (lowResilienceValue / totalValue) * 100 : 0;
    
    if (avgResilience < 4) {
      warnings.push(`Low average community resilience (${avgResilience.toFixed(1)}/10)`);
    }
    if (avgVulnerability > 6) {
      warnings.push(`High community vulnerability (${avgVulnerability.toFixed(1)}/10)`);
    }
    if (lowResPct > 30) {
      warnings.push(`${lowResPct.toFixed(0)}% of value in low-resilience areas`);
    }
    
    // Lowest resilience locations
    const lowestResilience = locationScores
      .sort((a, b) => a.resilience - b.resilience)
      .slice(0, 5);
    
    return {
      score,
      avgResilience,
      avgVulnerability,
      lowResilienceCount,
      lowResilienceValue,
      lowResPct,
      warnings,
      lowestResilience,
      totalValue
    };
  }, [items]);

  // ============================================================================
  // 4. INSURANCE COVERAGE ADEQUACY
  // ============================================================================
  const insuranceMetrics = useMemo(() => {
    if (items.length === 0) return { 
      score: 0, 
      floodExposedValue: 0,
      hurricaneExposedValue: 0,
      earthquakeExposedValue: 0,
      wildfireExposedValue: 0,
      multiHazardValue: 0,
      specialCoverageNeeded: 0,
      specialCoveragePct: 0,
      warnings: [] as string[], 
      topExposures: [] as { location: string; hazards: string[]; value: number; risk: number }[],
      totalValue: 0
    };
    
    let totalValue = 0;
    let floodExposedValue = 0;
    let hurricaneExposedValue = 0;
    let earthquakeExposedValue = 0;
    let wildfireExposedValue = 0;
    let multiHazardValue = 0;
    
    const exposureDetails: { location: string; hazards: string[]; value: number; risk: number }[] = [];
    
    items.forEach(item => {
      const value = normalizeAssetValue(item.asset_value);
      totalValue += value;
      
      const hazards: string[] = [];
      const flood = parseFloat(String(item.flood || 0));
      const hurricane = parseFloat(String(item.hurricane || 0));
      const earthquake = parseFloat(String(item.earthquake || 0));
      const wildfire = parseFloat(String(item.wildfire || 0));
      const tornado = parseFloat(String(item.tornado || 0));
      
      // High exposure threshold = 6
      if (flood > 6) { floodExposedValue += value; hazards.push('Flood'); }
      if (hurricane > 6) { hurricaneExposedValue += value; hazards.push('Hurricane'); }
      if (earthquake > 6) { earthquakeExposedValue += value; hazards.push('Earthquake'); }
      if (wildfire > 6) { wildfireExposedValue += value; hazards.push('Wildfire'); }
      if (tornado > 6) { hazards.push('Tornado'); }
      
      if (hazards.length >= 2) {
        multiHazardValue += value;
      }
      
      if (hazards.length > 0) {
        exposureDetails.push({
          location: `${item.city || 'Unknown'}, ${item.state || ''}`,
          hazards,
          value,
          risk: parseFloat(String(item.risk_score || 0))
        });
      }
    });
    
    // Calculate insurance adequacy score
    // Based on: diversity of exposure (more concentrated = worse)
    // Standard homeowner's doesn't cover flood, earthquake - these need special policies
    
    const specialCoverageNeeded = floodExposedValue + earthquakeExposedValue;
    const specialCoveragePct = totalValue > 0 ? (specialCoverageNeeded / totalValue) * 100 : 0;
    const multiHazardPct = totalValue > 0 ? (multiHazardValue / totalValue) * 100 : 0;
    
    // Score decreases as special coverage needs increase
    // Assume 50% special coverage need = 50 score
    let score = 100 - (specialCoveragePct * 0.5) - (multiHazardPct * 0.3);
    score = Math.max(0, Math.min(100, score));
    
    const warnings: string[] = [];
    
    if (floodExposedValue > 0) {
      const pct = (floodExposedValue / totalValue) * 100;
      warnings.push(`${formatCurrency(floodExposedValue)} (${pct.toFixed(0)}%) needs flood insurance`);
    }
    if (earthquakeExposedValue > 0) {
      const pct = (earthquakeExposedValue / totalValue) * 100;
      warnings.push(`${formatCurrency(earthquakeExposedValue)} (${pct.toFixed(0)}%) needs earthquake insurance`);
    }
    if (multiHazardPct > 20) {
      warnings.push(`${multiHazardPct.toFixed(0)}% exposed to multiple high-risk hazards`);
    }
    
    // Sort by value at risk
    const topExposures = exposureDetails
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
    
    return {
      score,
      floodExposedValue,
      hurricaneExposedValue,
      earthquakeExposedValue,
      wildfireExposedValue,
      multiHazardValue,
      specialCoverageNeeded,
      specialCoveragePct,
      warnings,
      topExposures,
      totalValue
    };
  }, [items]);

  // ============================================================================
  // OVERALL HEALTH SCORE
  // ============================================================================
  const overallScore = useMemo(() => {
    // Weighted average of all four scores
    const weights = {
      risk: 0.35,        // Risk is most important
      geo: 0.20,         // Geographic diversification
      resilience: 0.25,  // Community resilience
      insurance: 0.20    // Insurance adequacy
    };
    
    return (
      riskMetrics.score * weights.risk +
      geoMetrics.score * weights.geo +
      resilienceMetrics.score * weights.resilience +
      insuranceMetrics.score * weights.insurance
    );
  }, [riskMetrics.score, geoMetrics.score, resilienceMetrics.score, insuranceMetrics.score]);

  const overallColor = getScoreColor(overallScore);
  const overallGrade = getGrade(overallScore);

  if (items.length === 0) {
    return (
      <Alert severity="info">
        No portfolio data available. Add locations to see your Portfolio Health Score.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Overall Health Score Header */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: `linear-gradient(135deg, ${overallColor}10 0%, ${overallColor}05 100%)`,
          border: `2px solid ${overallColor}30`
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ 
                width: 140, 
                height: 140, 
                borderRadius: '50%', 
                border: `8px solid ${overallColor}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                bgcolor: 'white',
                boxShadow: `0 0 30px ${overallColor}30`
              }}>
                <Typography variant="h2" fontWeight={800} sx={{ color: overallColor, lineHeight: 1 }}>
                  {overallScore.toFixed(0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">out of 100</Typography>
              </Box>
              <Chip 
                label={`Grade: ${overallGrade}`}
                sx={{ 
                  mt: 2, 
                  bgcolor: overallColor, 
                  color: 'white', 
                  fontWeight: 700,
                  fontSize: '1rem',
                  px: 2
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Portfolio Health Score
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              A comprehensive assessment of your portfolio's climate risk profile, 
              geographic diversification, community resilience, and insurance coverage adequacy.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: getScoreColor(riskMetrics.score) }}>
                    {riskMetrics.score.toFixed(0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Risk Score</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: getScoreColor(geoMetrics.score) }}>
                    {geoMetrics.score.toFixed(0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Diversification</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: getScoreColor(resilienceMetrics.score) }}>
                    {resilienceMetrics.score.toFixed(0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Resilience</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: getScoreColor(insuranceMetrics.score) }}>
                    {insuranceMetrics.score.toFixed(0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Insurance</Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Individual Score Cards */}
      <Grid container spacing={3}>
        {/* Risk Score Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <ScoreCard
            title="Risk Score"
            score={riskMetrics.score}
            icon={<RiskIcon sx={{ color: getScoreColor(riskMetrics.score) }} />}
            color={getScoreColor(riskMetrics.score)}
            description={`Weighted avg risk: ${riskMetrics.weightedRisk.toFixed(1)}/10`}
            warnings={riskMetrics.warnings}
            onInfoClick={() => setDetailDialog('risk')}
          />
        </Grid>

        {/* Geographic Diversification Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <ScoreCard
            title="Geographic Diversification"
            score={geoMetrics.score}
            icon={<GeoIcon sx={{ color: getScoreColor(geoMetrics.score) }} />}
            color={getScoreColor(geoMetrics.score)}
            description={`${geoMetrics.stateCount} states, ${geoMetrics.regionCount} regions`}
            warnings={geoMetrics.warnings}
            onInfoClick={() => setDetailDialog('geo')}
          />
        </Grid>

        {/* Community Resilience Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <ScoreCard
            title="Community Resilience"
            score={resilienceMetrics.score}
            icon={<CommunityIcon sx={{ color: getScoreColor(resilienceMetrics.score) }} />}
            color={getScoreColor(resilienceMetrics.score)}
            description={`Avg resilience: ${(resilienceMetrics.avgResilience || 0).toFixed(1)}/10`}
            warnings={resilienceMetrics.warnings}
            onInfoClick={() => setDetailDialog('resilience')}
          />
        </Grid>

        {/* Insurance Adequacy Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <ScoreCard
            title="Insurance Adequacy"
            score={insuranceMetrics.score}
            icon={<InsuranceIcon sx={{ color: getScoreColor(insuranceMetrics.score) }} />}
            color={getScoreColor(insuranceMetrics.score)}
            description={`${(insuranceMetrics.specialCoveragePct || 0).toFixed(0)}% needs special coverage`}
            warnings={insuranceMetrics.warnings}
            onInfoClick={() => setDetailDialog('insurance')}
          />
        </Grid>
      </Grid>

      {/* Detail Dialogs */}
      {/* Risk Detail Dialog */}
      <Dialog open={detailDialog === 'risk'} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RiskIcon sx={{ color: getScoreColor(riskMetrics.score) }} />
            Risk Score Details
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#F9FAFB' }}>
                <Typography variant="h3" fontWeight={700} sx={{ color: getScoreColor(riskMetrics.score) }}>
                  {riskMetrics.score.toFixed(0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Overall Score</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" gutterBottom>Key Metrics</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Weighted Average Risk</TableCell>
                    <TableCell align="right"><strong>{riskMetrics.weightedRisk.toFixed(2)}/10</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>High-Risk Locations</TableCell>
                    <TableCell align="right"><strong>{riskMetrics.highRiskCount} of {items.length}</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>High-Risk Value</TableCell>
                    <TableCell align="right"><strong>{formatCurrency(riskMetrics.highRiskValue || 0)}</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>% Value at High Risk</TableCell>
                    <TableCell align="right"><strong>{(riskMetrics.highRiskPct || 0).toFixed(1)}%</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>Top Risk Concentrations (by Value × Risk)</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Risk Score</TableCell>
                  <TableCell align="right">Asset Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riskMetrics.topRiskLocations?.map((loc, i) => (
                  <TableRow key={i}>
                    <TableCell>{loc.location}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={loc.risk.toFixed(1)} 
                        size="small"
                        sx={{ 
                          bgcolor: loc.risk > 6 ? '#FEE2E2' : loc.risk > 3 ? '#FEF3C7' : '#D1FAE5',
                          color: loc.risk > 6 ? '#DC2626' : loc.risk > 3 ? '#D97706' : '#059669'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(loc.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Geographic Detail Dialog */}
      <Dialog open={detailDialog === 'geo'} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GeoIcon sx={{ color: getScoreColor(geoMetrics.score) }} />
            Geographic Diversification Details
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>By State</Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>State</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell align="right">% of Portfolio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {geoMetrics.stateBreakdown?.map((state, i) => (
                      <TableRow key={i}>
                        <TableCell>{state.state}</TableCell>
                        <TableCell align="right">{formatCurrency(state.value)}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={state.pct} 
                              sx={{ width: 60, height: 6, borderRadius: 3 }}
                            />
                            {state.pct.toFixed(1)}%
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>By Region</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Region</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell align="right">% of Portfolio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {geoMetrics.regionBreakdown?.map((region, i) => (
                      <TableRow key={i}>
                        <TableCell>{region.region}</TableCell>
                        <TableCell align="right">{formatCurrency(region.value)}</TableCell>
                        <TableCell align="right">{region.pct.toFixed(1)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Herfindahl-Hirschman Index (HHI):</strong> {geoMetrics.hhi?.toFixed(0)}
                  <br />
                  Lower HHI = more diversified. HHI &lt; 1500 is well diversified.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Resilience Detail Dialog */}
      <Dialog open={detailDialog === 'resilience'} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CommunityIcon sx={{ color: getScoreColor(resilienceMetrics.score) }} />
            Community Resilience Details
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#F9FAFB', mb: 2 }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {(resilienceMetrics.avgResilience || 0).toFixed(1)}/10
                </Typography>
                <Typography variant="body2" color="text.secondary">Avg Resilience</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#FEF3C7' }}>
                <Typography variant="h4" fontWeight={700} color="warning.main">
                  {(resilienceMetrics.avgVulnerability || 0).toFixed(1)}/10
                </Typography>
                <Typography variant="body2" color="text.secondary">Avg Vulnerability</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" gutterBottom>Lowest Resilience Locations</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                      <TableCell>Location</TableCell>
                      <TableCell align="right">Resilience</TableCell>
                      <TableCell align="right">Vulnerability</TableCell>
                      <TableCell align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resilienceMetrics.lowestResilience?.map((loc, i) => (
                      <TableRow key={i}>
                        <TableCell>{loc.location}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={loc.resilience.toFixed(1)} 
                            size="small"
                            sx={{ 
                              bgcolor: loc.resilience < 4 ? '#FEE2E2' : loc.resilience < 6 ? '#FEF3C7' : '#D1FAE5',
                              color: loc.resilience < 4 ? '#DC2626' : loc.resilience < 6 ? '#D97706' : '#059669'
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{loc.vulnerability.toFixed(1)}</TableCell>
                        <TableCell align="right">{formatCurrency(loc.value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Community Resilience</strong> measures a community's ability to prepare for, respond to, and recover from disasters.
              <strong> Community Vulnerability</strong> indicates socioeconomic factors that may impede recovery.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Insurance Detail Dialog */}
      <Dialog open={detailDialog === 'insurance'} onClose={() => setDetailDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InsuranceIcon sx={{ color: getScoreColor(insuranceMetrics.score) }} />
            Insurance Coverage Analysis
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Standard property insurance typically <strong>excludes</strong> flood and earthquake damage. 
              Properties with high exposure to these hazards require separate policies.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Special Coverage Needs</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#3B82F6', borderRadius: '50%' }} />
                        Flood Insurance Needed
                      </Box>
                    </TableCell>
                    <TableCell align="right"><strong>{formatCurrency(insuranceMetrics.floodExposedValue || 0)}</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#8B5CF6', borderRadius: '50%' }} />
                        Earthquake Insurance Needed
                      </Box>
                    </TableCell>
                    <TableCell align="right"><strong>{formatCurrency(insuranceMetrics.earthquakeExposedValue || 0)}</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#06B6D4', borderRadius: '50%' }} />
                        Hurricane Exposed
                      </Box>
                    </TableCell>
                    <TableCell align="right"><strong>{formatCurrency(insuranceMetrics.hurricaneExposedValue || 0)}</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#F97316', borderRadius: '50%' }} />
                        Wildfire Exposed
                      </Box>
                    </TableCell>
                    <TableCell align="right"><strong>{formatCurrency(insuranceMetrics.wildfireExposedValue || 0)}</strong></TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: '#FEF3C7' }}>
                    <TableCell><strong>Multi-Hazard Exposure</strong></TableCell>
                    <TableCell align="right"><strong>{formatCurrency(insuranceMetrics.multiHazardValue || 0)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Highest Exposure Locations</Typography>
              <TableContainer sx={{ maxHeight: 250 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Location</TableCell>
                      <TableCell>Hazards</TableCell>
                      <TableCell align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {insuranceMetrics.topExposures?.slice(0, 8).map((loc, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{loc.location}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {loc.hazards.map((h, j) => (
                              <Chip key={j} label={h} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrency(loc.value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}